import { useQuery } from '@apollo/client';
import { EWorldMapSetType } from '__generated__/globalTypes';
import {
    WorldMapMarkerStory,
    WorldMapMarkerStoryVariables
} from 'graphql/query/__generated__/WorldMapMarkerStory';
import { WORLD_MAP_MARKER_STORY_QUERY } from 'graphql/query/worldQuery';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { useContext } from 'react';
import { IWorldMarker, isV2Marker } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';

export const useMarkerVisibility = () => {
    const ctxEdit = useContext(CtxEdit);
    const { getTokenForMapId } = useWorldMapLocalStorage();

    const worldMapId = ctxEdit.map?.worldMapId;

    const markersResult = useQuery<WorldMapMarkerStory, WorldMapMarkerStoryVariables>(
        WORLD_MAP_MARKER_STORY_QUERY,
        {
            variables: { worldMapId, token: getTokenForMapId(worldMapId) },
            skip: !worldMapId,
            fetchPolicy: 'cache-first'
        }
    );

    const markers = markersResult.data?.worldMapView?.markers ?? [];

    // We compute the hidden data. That is for new markers that do not appear in
    // any of the sets when they are created to be always visible (never hihden)
    const hiddenMarkersBySetCategory = new Set(
        markers
            .filter((marker) => {
                // If it's not in any CATEGORY set, then it's always visible
                const sets =
                    marker.worldMapSets.filter((e) => e.type === EWorldMapSetType.CATEGORY) ?? [];
                if (sets.length === 0) {
                    return false;
                }
                return sets.every((set) => !ctxEdit.visibleMapSetIds.includes(set.worldMapSetId));
            })
            .map((e) => e.worldMapMarkerId)
    );

    const hiddenMarkersBySetFloor = new Set(
        markers
            .filter((marker) => {
                // If it's not in any FLOOR set, then it's always visible
                const sets =
                    marker.worldMapSets.filter((e) => e.type === EWorldMapSetType.FLOOR) ?? [];
                if (sets.length === 0) {
                    return false;
                }
                return sets.every((set) => !ctxEdit.visibleMapSetIds.includes(set.worldMapSetId));
            })
            .map((e) => e.worldMapMarkerId)
    );

    const isMarkerVisibleinLayerList = (marker: IWorldMarker) => {
        const isAllowedForSetVisibility =
            !hiddenMarkersBySetCategory.has(marker.id) && !hiddenMarkersBySetFloor.has(marker.id);
        return isAllowedForSetVisibility;
    };

    const isMarkerVisibleInEdit = (marker: IWorldMarker) => {
        const isV2 = isV2Marker(marker);

        const isAllowedForOwnVisibility = marker.visible;
        const isAllowedForSetVisibility =
            !hiddenMarkersBySetCategory.has(marker.id) && !hiddenMarkersBySetFloor.has(marker.id);

        if (isV2) {
            return isAllowedForOwnVisibility && isAllowedForSetVisibility;
        } else {
            // V1 markers are not visible when they are selected
            const singleMarkerSelection = ctxEdit.selectedMarkers.length === 1;
            const isAllowedBecauseNotSelected = singleMarkerSelection
                ? !ctxEdit.selectedMarkers.includes(marker.id)
                : true;

            return (
                isAllowedForOwnVisibility &&
                isAllowedForSetVisibility &&
                isAllowedBecauseNotSelected
            );
        }
    };

    const isMarkerVisibleForSelectionInEdit = (marker: IWorldMarker) =>
        marker.visible &&
        marker.selectable &&
        !hiddenMarkersBySetCategory.has(marker.id) &&
        !hiddenMarkersBySetFloor.has(marker.id);

    return {
        isMarkerVisibleInEdit,
        isMarkerVisibleForSelectionInEdit,
        isMarkerVisibleinLayerList
    };
};
